import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import React from 'react';

export type Option = {
  labelChild?: string;
  label: string;
  value: string;
  [key: string]: string | undefined;
};

type SelectSearchProps = {
  placeholder: string;
  emptyMessage: string;
  options: Option[];
  isLoading: boolean;
  onInputChange: (value: string) => void;
  onSelect: (option: Option) => void;
  disabled?: boolean;
  className?: string;
  classNameWrapper?: string;
  defaultInputValue?: string;
  isCommandInput?: boolean;
};

export const SelectSearch: React.FC<SelectSearchProps> = ({
  options,
  emptyMessage = 'No results.',
  onInputChange,
  onSelect,
  isLoading,
  disabled,
  placeholder = 'Find something...',
  className,
  classNameWrapper,
  defaultInputValue = '',
  isCommandInput = false,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState<string>(defaultInputValue);
  const [selected, setSelected] = React.useState<Option | null>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const inputAreaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    setInputValue(defaultInputValue);
  }, [defaultInputValue]);

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      onInputChange(value);
      setIsOpen(true);
    },
    [onInputChange]
  );

  const handleSelectOption = React.useCallback(
    (option: Option) => {
      setSelected(option);
      setInputValue(option.label);
      onSelect(option);
      setIsOpen(false);
    },
    [onSelect]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && inputValue) {
        const matchedOption = options.find(
          (option) => option.label.toLowerCase() === inputValue.toLowerCase()
        );
        if (matchedOption) {
          handleSelectOption(matchedOption);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [inputValue, options, handleSelectOption]
  );

  const handleBlur = React.useCallback(() => {
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  return (
    <CommandPrimitive onKeyDown={handleKeyDown} shouldFilter={false}>
      <div>
        {isCommandInput ? (
          <CommandInput
            ref={inputRef}
            value={inputValue}
            onValueChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('text-base', className)}
          />
        ) : (
          <Textarea
            ref={inputAreaRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={handleBlur}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            rows={2}
            className={cn('text-sm', className)}
          />
        )}
      </div>
      <div className={cn('relative mt-1', classNameWrapper)}>
        {isOpen && (
          <div className='absolute top-0 z-30 w-full rounded-xl bg-white outline-none animate-in fade-in-0 zoom-in-95'>
            <CommandList className='rounded-lg ring-1 ring-slate-200'>
              {isLoading ? (
                <CommandPrimitive.Loading>
                  <div className='flex justify-center p-3'>
                    <LoadingAnimationIcon color='gray-400' />
                  </div>
                </CommandPrimitive.Loading>
              ) : options.length > 0 ? (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        'flex w-full items-center gap-2',
                        selected?.value !== option.value ? 'pl-8' : ''
                      )}
                    >
                      {selected?.value === option.value && <Check className='w-4' />}
                      <div className='flex flex-col'>
                        <span>{option.label}</span>
                        {option.labelChild && (
                          <span className='text-xs text-gray-500'>{option.labelChild}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                <CommandPrimitive.Empty className='select-none rounded-sm px-2 py-3 text-center text-sm'>
                  {emptyMessage}
                </CommandPrimitive.Empty>
              )}
            </CommandList>
          </div>
        )}
      </div>
    </CommandPrimitive>
  );
};
